import {
  ActivityType,
  BlockType,
  ResourceType,
  ActivitySectionType,
  type ReadingVariant,
} from "../../../types";
import type {
  AlternativeInput,
  CreateLessonInput,
} from "../../../types/api/createLesson";
import type {
  LessonResponse,
  Block as ApiBlock,
} from "../../../types/api/getLesson";
import type { ResourceFileResponse } from "../../../types/api/getResources";

export const resolveBlockType = (blockType: BlockType) => {
  switch (blockType) {
    case BlockType.TEXT:
      return "Texto";
    case BlockType.TEXT_WITH_IMAGE_SMALL:
      return "Texto con imagen lateral";
    case BlockType.TEXT_WITH_IMAGE_BIG:
      return "Texto con imagen inferior";
    case BlockType.IMAGE:
      return "Imagen";
    case BlockType.TEXT_WITH_RESOURCE:
      return "Texto con recurso";
    case BlockType.TEXT_WITH_RESOURCE_AND_IMAGE:
      return "Texto con recurso e imagen inferior";
    case BlockType.HOMEWORK:
      return "Tarea";
    default:
      return "Texto";
  }
};

export const resolveActivityType = (activityType: ActivityType) => {
  switch (activityType) {
    case ActivityType.START:
      return "Inicio";
    case ActivityType.MIDDLE:
      return "Desarrollo";
    case ActivityType.CLOSING:
      return "Cierre";

    default:
      return "Inicio";
  }
};

export const resolveResourceType = (resourceType: ResourceType) => {
  switch (resourceType) {
    case ResourceType.VIDEO:
      return "Vídeo";
    case ResourceType.SLIDES:
      return "Diapositiva";
    case ResourceType.BOOK:
      return "Libro";
    case ResourceType.AUDIO:
      return "Audio";
    case ResourceType.QUIZ:
      return "Quiz";
    case ResourceType.GAME:
      return "Juego";
    case ResourceType.DOWNLOAD:
      return "Descarga";
    case ResourceType.READING:
      return "Lectura";
    case ResourceType.LITERACY_WORKSHEET:
      return "Ficha de trabajo de lectoescritura";
    case ResourceType.GAME_QUIZ:
      return "Quizzes Emmi";
    case ResourceType.DIAGNOSTIC:
      return "Diagnóstico";
    default:
      return "Vídeo";
  }
};

/**
 * UI-only value used in the resource type dropdown.
 * "READING_KINDER" and "READING_PRIMARIA" both map to ResourceType.READING on submit.
 */
export type ResourceTypeDropdownValue =
  ResourceType | "READING_KINDER" | "READING_PRIMARIA";

export interface ResourceTypeOption {
  value: ResourceTypeDropdownValue;
  label: string;
}

/**
 * Returns dropdown options with READING split into Kinder and Primaria.
 *
 * ResourceType.QUIZ is never offered: it is a legacy value kept only for
 * rows still present in the DB (see ResourceType). A resource whose stored
 * type is QUIZ renders that raw value in the Select until data cleanup.
 */
export const getResourceTypeOptions = (): ResourceTypeOption[] => {
  const options: ResourceTypeOption[] = [];
  for (const type of Object.values(ResourceType)) {
    if (type === ResourceType.QUIZ) {
      continue;
    }
    if (type === ResourceType.READING) {
      options.push({ value: "READING_KINDER", label: "Lectura Kinder" });
      options.push({ value: "READING_PRIMARIA", label: "Lectura Primaria" });
    } else {
      options.push({ value: type, label: resolveResourceType(type) });
    }
  }
  return options;
};

/**
 * Maps a dropdown value back to the actual ResourceType and optional ReadingVariant.
 */
export const parseDropdownValue = (
  value: ResourceTypeDropdownValue,
): { type: ResourceType; readingVariant: ReadingVariant | null } => {
  if (value === "READING_KINDER") {
    return { type: ResourceType.READING, readingVariant: "kinder" };
  }
  if (value === "READING_PRIMARIA") {
    return { type: ResourceType.READING, readingVariant: "primaria" };
  }
  return { type: value as ResourceType, readingVariant: null };
};

/**
 * Returns the dropdown value for a given ResourceType + ReadingVariant.
 */
export const toDropdownValue = (
  type: ResourceType,
  readingVariant: ReadingVariant | null,
): ResourceTypeDropdownValue => {
  if (type === ResourceType.READING) {
    return readingVariant === "kinder" ? "READING_KINDER" : "READING_PRIMARIA";
  }
  return type;
};

export const getDownloadFileLabel = (file: ResourceFileResponse): string => {
  if (file.file_type === "PICTURE_CARD") {
    return "Picture Cards";
  }
  return file.label || `PDF ${file.order_by + 1}`;
};

export enum ActivityMainDevelopmentDomain {
  READING = "READING", // LECTURA
  ORAL_EXPRESSION = "ORAL_EXPRESSION", // EXPRESIÓN ORAL
  WRITING = "WRITING", // ESCRITURA
  MOTOR_SKILLS = "MOTOR_SKILLS", // MOTRICIDAD
  PHONOLOGICAL = "PHONOLOGICAL", // FONOLÓGICO
  SOCIO_EMOTIONAL = "SOCIO_EMOTIONAL", // SOCIOEMOCIONAL
  COGNITIVE = "COGNITIVE", // COGNITIVO
  CREATIVE = "CREATIVE", // CREATIVO
  LISTENING_COMPREHENSION = "LISTENING_COMPREHENSION", // COMPRENSIÓN AUDITIVA
  VOCABULARY = "VOCABULARY", // VOCABULARIO
}

export enum ActivityInteractionType {
  INDIVIDUAL = "INDIVIDUAL", // INDIVIDUAL
  GROUP = "GROUP", // PAREJAS O EQUIPOS
  CLASSROOM = "CLASSROOM", // CLASE ENTERA
}

export enum ActivityTypeOfEducationalActivity {
  MOVEMENT = "MOVEMENT", // MOVIMIENTO
  ORAL_RESPONSE = "ORAL_RESPONSE", // RESPUESTA ORAL
  ACTIVE_LISTENING = "ACTIVE_LISTENING", // ESCUCHA ACTIVA
  QUESTION_ANSWER = "QUESTION_ANSWER", // PREGUNTA-RESPUESTA
  MATERIAL_CREATION = "MATERIAL_CREATION", // CREACIÓN CON MATERIALES
  VISUAL_EXPLORATION = "VISUAL_EXPLORATION", // EXPLORACIÓN VISUAL
  TOUCH = "TOUCH", // TACTO
  PROBLEM_SOLVING = "PROBLEM_SOLVING", // RESOLUCIÓN DE PROBLEMAS
}

export enum ActivityBloomLevel {
  REMEMBER = "REMEMBER",
  UNDERSTAND = "UNDERSTAND",
  APPLY = "APPLY",
  ANALYZE = "ANALYZE",
  EVALUATE = "EVALUATE",
  CREATE = "CREATE",
}

export enum ActivityPrimariaInteractionType {
  INDIVIDUAL = "INDIVIDUAL",
  PAIR = "PAIR",
  TEAM = "TEAM",
  WHOLE_GROUP = "WHOLE_GROUP",
}

export enum ActivityEvidenceType {
  ORAL = "ORAL",
  WRITTEN = "WRITTEN",
  MANIPULATIVE = "MANIPULATIVE",
  GRAPHIC = "GRAPHIC",
  PHYSICAL = "PHYSICAL",
}

export enum ActivityNature {
  OPEN_EXPLORATION = "OPEN_EXPLORATION",
  STRUCTURED_PRACTICE = "STRUCTURED_PRACTICE",
  CONTEXTUAL_APPLICATION = "CONTEXTUAL_APPLICATION",
  PROBLEM_SOLVING = "PROBLEM_SOLVING",
  CREATIVE_PRODUCTION = "CREATIVE_PRODUCTION",
  METACOGNITIVE_REFLECTION = "METACOGNITIVE_REFLECTION",
}

export const resolveActivityMainDevelopmentDomain = (
  mainDevelopmentDomain: ActivityMainDevelopmentDomain,
) => {
  switch (mainDevelopmentDomain) {
    case ActivityMainDevelopmentDomain.READING:
      return "Lectura";
    case ActivityMainDevelopmentDomain.ORAL_EXPRESSION:
      return "Expresión Oral";
    case ActivityMainDevelopmentDomain.WRITING:
      return "Escritura";
    case ActivityMainDevelopmentDomain.MOTOR_SKILLS:
      return "Motricidad";
    case ActivityMainDevelopmentDomain.PHONOLOGICAL:
      return "Fonológico";
    case ActivityMainDevelopmentDomain.SOCIO_EMOTIONAL:
      return "Socioemocional";
    case ActivityMainDevelopmentDomain.COGNITIVE:
      return "Cognitivo";
    case ActivityMainDevelopmentDomain.CREATIVE:
      return "Creativo";
    case ActivityMainDevelopmentDomain.LISTENING_COMPREHENSION:
      return "Comprensión Auditiva";
    case ActivityMainDevelopmentDomain.VOCABULARY:
      return "Vocabulario";
    default:
      return "Lectura";
  }
};

export const resolveActivityInteractionType = (
  interactionType: ActivityInteractionType,
) => {
  switch (interactionType) {
    case ActivityInteractionType.INDIVIDUAL:
      return "Individual";
    case ActivityInteractionType.GROUP:
      return "Parejas o equipos";
    case ActivityInteractionType.CLASSROOM:
      return "Clase entera";
    default:
      return "Individual";
  }
};

export const resolveActivityTypeOfEducationalActivity = (
  typeOfEducationalActivity: ActivityTypeOfEducationalActivity,
) => {
  switch (typeOfEducationalActivity) {
    case ActivityTypeOfEducationalActivity.MOVEMENT:
      return "Movimiento";
    case ActivityTypeOfEducationalActivity.ORAL_RESPONSE:
      return "Respuesta Oral";
    case ActivityTypeOfEducationalActivity.ACTIVE_LISTENING:
      return "Escucha Activa";
    case ActivityTypeOfEducationalActivity.QUESTION_ANSWER:
      return "Pregunta-Respuesta";
    case ActivityTypeOfEducationalActivity.MATERIAL_CREATION:
      return "Creación con Materiales";
    case ActivityTypeOfEducationalActivity.VISUAL_EXPLORATION:
      return "Exploración Visual";
    case ActivityTypeOfEducationalActivity.TOUCH:
      return "Tacto";
    case ActivityTypeOfEducationalActivity.PROBLEM_SOLVING:
      return "Resolución de Problemas";
    default:
      return "Movimiento";
  }
};

export const resolveActivityBloomLevel = (
  value: ActivityBloomLevel,
): string => {
  const labels: Record<ActivityBloomLevel, string> = {
    [ActivityBloomLevel.REMEMBER]: "Recordar",
    [ActivityBloomLevel.UNDERSTAND]: "Comprender",
    [ActivityBloomLevel.APPLY]: "Aplicar",
    [ActivityBloomLevel.ANALYZE]: "Analizar",
    [ActivityBloomLevel.EVALUATE]: "Evaluar",
    [ActivityBloomLevel.CREATE]: "Crear",
  };
  return labels[value] ?? value;
};

export const resolveActivityPrimariaInteractionType = (
  value: ActivityPrimariaInteractionType,
): string => {
  const labels: Record<ActivityPrimariaInteractionType, string> = {
    [ActivityPrimariaInteractionType.INDIVIDUAL]: "Individual",
    [ActivityPrimariaInteractionType.PAIR]: "Pareja",
    [ActivityPrimariaInteractionType.TEAM]: "Equipo",
    [ActivityPrimariaInteractionType.WHOLE_GROUP]: "Grupo completo",
  };
  return labels[value] ?? value;
};

export const resolveActivityEvidenceType = (
  value: ActivityEvidenceType,
): string => {
  const labels: Record<ActivityEvidenceType, string> = {
    [ActivityEvidenceType.ORAL]: "Oral",
    [ActivityEvidenceType.WRITTEN]: "Escrita",
    [ActivityEvidenceType.MANIPULATIVE]: "Manipulativa",
    [ActivityEvidenceType.GRAPHIC]: "Gráfica",
    [ActivityEvidenceType.PHYSICAL]: "Corporal",
  };
  return labels[value] ?? value;
};

export const resolveActivityNature = (value: ActivityNature): string => {
  const labels: Record<ActivityNature, string> = {
    [ActivityNature.OPEN_EXPLORATION]: "Exploración abierta",
    [ActivityNature.STRUCTURED_PRACTICE]: "Práctica estructurada",
    [ActivityNature.CONTEXTUAL_APPLICATION]: "Aplicación contextual",
    [ActivityNature.PROBLEM_SOLVING]: "Resolución de problemas",
    [ActivityNature.CREATIVE_PRODUCTION]: "Producción creativa",
    [ActivityNature.METACOGNITIVE_REFLECTION]: "Reflexión metacognitiva",
  };
  return labels[value] ?? value;
};

export const SECTION_TITLES: Record<ActivitySectionType, string> = {
  [ActivitySectionType.GUIDED_DEMONSTRATION]: "Demostración guiada",
  [ActivitySectionType.HANDS_ON]: "Manos a la obra",
  [ActivitySectionType.GUIDED_EXPLORATION]: "Exploración acompañada",
};

export const initialLesson: FormLessonUI = {
  title: "",
  education_level: undefined,
  objectives_lesson: "",
  final_product_contribution: "",
  summary: "",
  activities: [],
};

export const BLOCK_TYPE_OPTIONS: Record<
  BlockType,
  { label: string; hasText: boolean; hasImage: boolean; hasResource: boolean }
> = {
  [BlockType.TEXT]: {
    label: "Texto",
    hasText: true,
    hasImage: false,
    hasResource: false,
  },
  [BlockType.TEXT_WITH_IMAGE_BIG]: {
    label: "Texto con imagen inferior",
    hasText: true,
    hasImage: true,
    hasResource: false,
  },
  [BlockType.TEXT_WITH_IMAGE_SMALL]: {
    label: "Texto con imagen lateral",
    hasText: true,
    hasImage: true,
    hasResource: false,
  },
  [BlockType.TEXT_WITH_RESOURCE]: {
    label: "Texto con recurso",
    hasText: true,
    hasImage: false,
    hasResource: true,
  },
  [BlockType.TEXT_WITH_RESOURCE_AND_IMAGE]: {
    label: "Texto con recurso e imagen inferior",
    hasText: true,
    hasImage: true,
    hasResource: true,
  },
  [BlockType.IMAGE]: {
    label: "Imagen",
    hasText: false,
    hasImage: true,
    hasResource: false,
  },
  [BlockType.HOMEWORK]: {
    label: "Tarea",
    hasText: true,
    hasImage: false,
    hasResource: false,
  },
};

export interface FormBlockUI {
  id?: number; // Opcional porque algunos bloques no tienen ID
  type: BlockType; // Tipo del bloque
  text: string; // Texto del bloque
  file_id: string | null; // ID del archivo asociado (si existe)
  file_id_url: string | null; // ID del archivo asociado (si existe)
  page_number?: number | null; // Página del recurso (slides / book)
  materials: string | null; // Información adicional del bloque
  active_methodology_tip?: string | null; // Tip de metodología activa
  resourceId: number | null; // ID del recurso asociado (si existe)
  resourceFileId?: number | null; // ID del archivo específico del recurso (para recursos multi-archivo)
  resourceType?: ResourceType; // Tipo de recurso asociado (si existe)
  order_by?: number; // Orden del bloque
}

export interface FormSectionUI {
  id?: number; // Opcional porque algunas secciones no tienen ID
  section_type: ActivitySectionType; // Tipo de sección
  order_by: number; // Orden de la sección
  blocks: FormBlockUI[]; // Lista de bloques dentro de la sección
}

/**
 * SCRUM-795: una alternativa sin manipulativos dentro del formulario.
 *
 * Tiene los mismos campos pedagógicos que una actividad y bloques planos. No
 * tiene `activityType` (hereda la posición de su actividad padre en la OD),
 * `sections` ni el toggle de manipulativos: una alternativa existe justamente
 * porque la versión manipulativa no se puede usar.
 */
export interface FormAlternativeUI {
  id?: number; // Ausente mientras la alternativa no se guardó
  title: string;
  duration: number;
  is_homework?: boolean;
  is_socioemotional: boolean;
  socioemotional_main_goal: string;
  socioemotional_support_goal: string;
  summary?: string;
  active_methodology_tip?: string;
  blocks: FormBlockUI[];
  mainDevelopmentDomains: ActivityMainDevelopmentDomain[];
  interactionTypes: ActivityInteractionType[];
  typesOfEducationalActivities: ActivityTypeOfEducationalActivity[];
  bloom_level?: ActivityBloomLevel[];
  primaria_interaction_type?: ActivityPrimariaInteractionType[];
  evidence_type?: ActivityEvidenceType[];
  activity_nature?: ActivityNature[];
}

export interface FormActivityUI {
  id?: number; // Opcional porque algunas actividades no tienen ID
  title: string; // Título de la actividad
  duration: number; // Duración de la actividad en minutos
  activityType: ActivityType; // Tipo de actividad (START, MIDDLE, CLOSING)
  is_homework?: boolean; // Marca si la actividad es una tarea
  has_manipulatives: boolean; // Marca si la actividad tiene manipulativos
  persisted_has_manipulatives?: boolean; // Valor guardado en backend: una vez guardada con manipulativos no se puede revertir
  is_socioemotional: boolean; // Marca si la actividad es socioemocional
  socioemotional_main_goal: string; // Meta socioemocional principal
  socioemotional_support_goal: string; // Meta socioemocional de apoyo
  summary?: string; // Resumen de la actividad generado por IA o manualmente
  manipulative_materials_list?: string; // Materiales identificados para actividades con manipulativos
  active_methodology_tip?: string; // Tip de metodología activa
  blocks: FormBlockUI[]; // Lista de bloques dentro de la actividad (vacío si has_manipulatives = true)
  sections: FormSectionUI[]; // Lista de secciones si has_manipulatives = true
  mainDevelopmentDomains: ActivityMainDevelopmentDomain[];
  interactionTypes: ActivityInteractionType[];
  typesOfEducationalActivities: ActivityTypeOfEducationalActivity[];
  bloom_level?: ActivityBloomLevel[];
  primaria_interaction_type?: ActivityPrimariaInteractionType[];
  evidence_type?: ActivityEvidenceType[];
  activity_nature?: ActivityNature[];
  /**
   * SCRUM-795: alternativas sin manipulativos, sólo para actividades
   * manipulativas. `undefined` significa "este formulario no las cargó" y hace
   * que el guardado no las toque; un array vacío sí las borra.
   */
  alternatives?: FormAlternativeUI[];
}

export interface FormLessonUI {
  title: string; // Título de la lección
  education_level?: "KINDER" | "PRIMARIA"; // Education level (preescolar / primaria)
  objectives_lesson?: string; // Objetivo de la lección
  final_product_contribution?: string; // Contribución al producto final
  summary?: string; // Resumen general de la lección
  activities: FormActivityUI[]; // Lista de actividades dentro de la lección
}

export function activityHasGame(activity: FormActivityUI): boolean {
  const allBlocks = activity.has_manipulatives
    ? activity.sections.flatMap((s) => s.blocks)
    : activity.blocks;
  return allBlocks.some((b) => b.resourceType === ResourceType.GAME);
}

/** SCRUM-795: un bloque de la API tal como lo necesita el formulario. */
const parseApiBlockToForm = (block: ApiBlock): FormBlockUI => ({
  id: block.id,
  type: block.type,
  order_by: block.order_by,
  text: block.text || "",
  file_id: block.file_id,
  file_id_url: block.file_id_url,
  page_number: block.page_number ?? null,
  materials: block.materials,
  active_methodology_tip: block.active_methodology_tip ?? null,
  resourceId: block.resource?.id ?? null,
  resourceType: block?.resource?.type,
  resourceFileId: block.resource_file?.id ?? null,
});

/**
 * SCRUM-795: los ids que el formulario inventa para filas nuevas salen de
 * `Date.now()`, así que quedan muy por encima de cualquier id real de la base.
 * No deben viajar al backend: si lo hicieran, tendría que adivinar que no son
 * suyos.
 */
const TEMPORARY_ID_FLOOR = 1000000000;

const persistedIdOrUndefined = (id?: number): number | undefined =>
  id !== undefined && id < TEMPORARY_ID_FLOOR ? id : undefined;

/**
 * SCRUM-795: convierte las alternativas del formulario al payload de la API.
 *
 * El orden es el del array y los bloques se renumeran por su posición, igual
 * que en una actividad normal. `keepIds` es false al crear la lección, cuando
 * todavía no existe ningún id que conservar.
 */
const buildAlternativesInput = (
  alternatives: FormAlternativeUI[],
  keepIds: boolean,
): AlternativeInput[] =>
  alternatives.map((alternative) => ({
    ...(keepIds ? { id: persistedIdOrUndefined(alternative.id) } : {}),
    title: alternative.title,
    duration: alternative.duration,
    is_homework: alternative.is_homework || false,
    is_socioemotional: alternative.is_socioemotional || false,
    socioemotional_main_goal: alternative.is_socioemotional
      ? alternative.socioemotional_main_goal || undefined
      : undefined,
    socioemotional_support_goal: alternative.is_socioemotional
      ? alternative.socioemotional_support_goal || undefined
      : undefined,
    summary: alternative.summary || undefined,
    active_methodology_tip: alternative.active_methodology_tip || undefined,
    mainDevelopmentDomains: alternative.mainDevelopmentDomains || [],
    interactionTypes: alternative.interactionTypes || [],
    typesOfEducationalActivities:
      alternative.typesOfEducationalActivities || [],
    bloom_level: alternative.bloom_level || [],
    primaria_interaction_type: alternative.primaria_interaction_type || [],
    evidence_type: alternative.evidence_type || [],
    activity_nature: alternative.activity_nature || [],
    blocks: alternative.blocks.map((block, blockIndex) => ({
      ...(keepIds ? { id: persistedIdOrUndefined(block.id) } : {}),
      type: block.type,
      order_by: blockIndex,
      text: block.text,
      file_id: block.file_id,
      page_number: block.page_number ?? null,
      materials: block.materials,
      active_methodology_tip: block.active_methodology_tip ?? null,
      resourceId: block.resourceId,
      resourceFileId: block.resourceFileId,
    })),
  }));

/**
 * SCRUM-795: la porción `alternatives` del payload de una actividad.
 *
 * Devuelve un objeto vacío — es decir, omite el campo — cuando la actividad no
 * es manipulativa o cuando el formulario nunca cargó las alternativas. El
 * backend distingue "no lo toqué" (campo ausente) de "no hay ninguna" (array
 * vacío), y perder esa diferencia borraría alternativas al guardar desde una
 * pantalla que no las conoce.
 */
const alternativesPayload = (
  activity: FormActivityUI,
  keepIds: boolean,
): { alternatives?: AlternativeInput[] } => {
  if (!activity.has_manipulatives || activity.alternatives === undefined) {
    return {};
  }

  return {
    alternatives: buildAlternativesInput(activity.alternatives, keepIds),
  };
};

export const parseLessonResponse = (response: LessonResponse): FormLessonUI => {
  const { id, created_at, updated_at, deleted_at, ...rest } = response;
  return {
    ...rest,
    title: response.title || "",
    education_level: response.education_level || "KINDER",
    objectives_lesson: response.objectives_lesson || "",
    final_product_contribution: response.final_product_contribution || "",
    summary: response.summary || "",
    activities: response.activities
      .map((activity) => ({
        ...activity,
        activityType: activity.type || ActivityType.START,
        is_homework: activity.is_homework || false,
        has_manipulatives: activity.has_manipulatives || false,
        persisted_has_manipulatives: activity.has_manipulatives || false,
        is_socioemotional: activity.is_socioemotional || false,
        socioemotional_main_goal: activity.socioemotional_main_goal || "",
        socioemotional_support_goal: activity.socioemotional_support_goal || "",
        summary: activity.summary || "",
        manipulative_materials_list: activity.manipulative_materials_list || "",
        active_methodology_tip: activity.active_methodology_tip || "",
        mainDevelopmentDomains: activity.mainDevelopmentDomains || [],
        interactionTypes: activity.interactionTypes || [],
        typesOfEducationalActivities:
          activity.typesOfEducationalActivities || [],
        bloom_level: activity.bloom_level || [],
        primaria_interaction_type: activity.primaria_interaction_type || [],
        evidence_type: activity.evidence_type || [],
        activity_nature: activity.activity_nature || [],
        blocks: activity.blocks.map(parseApiBlockToForm),
        // SCRUM-795: siempre un array — así el guardado sabe que el formulario
        // sí cargó las alternativas y puede distinguir "no hay ninguna" de
        // "no las toqué".
        alternatives: (activity.alternatives || []).map((alternative) => ({
          id: alternative.id,
          title: alternative.title || "",
          duration: alternative.duration,
          is_homework: alternative.is_homework || false,
          is_socioemotional: alternative.is_socioemotional || false,
          socioemotional_main_goal: alternative.socioemotional_main_goal || "",
          socioemotional_support_goal:
            alternative.socioemotional_support_goal || "",
          summary: alternative.summary || "",
          mainDevelopmentDomains: alternative.mainDevelopmentDomains || [],
          interactionTypes: alternative.interactionTypes || [],
          typesOfEducationalActivities:
            alternative.typesOfEducationalActivities || [],
          bloom_level: alternative.bloom_level || [],
          primaria_interaction_type:
            alternative.primaria_interaction_type || [],
          evidence_type: alternative.evidence_type || [],
          activity_nature: alternative.activity_nature || [],
          blocks: alternative.blocks.map(parseApiBlockToForm),
        })),
        sections:
          activity.sections?.map((section) => ({
            id: section.id,
            section_type: section.section_type,
            order_by: section.order_by,
            blocks: section.blocks.map((block: ApiBlock) => ({
              id: block.id,
              type: block.type,
              order_by: block.order_by,
              text: block.text || "",
              file_id: block.file_id,
              file_id_url: block.file_id_url,
              page_number: block.page_number ?? null,
              materials: block.materials,
              active_methodology_tip: block.active_methodology_tip ?? null,
              resourceId: block.resource?.id ?? null,
              resourceType: block?.resource?.type,
              // Mapear el archivo específico seleccionado (si existe) al campo resourceFileId
              resourceFileId: block.resource_file?.id ?? null,
            })),
          })) || [],
      }))
      .filter((activity) => !isActivityEmpty(activity)),
  };
};

export const parseLessonToCreateLessonInput = (
  lesson: FormLessonUI,
): CreateLessonInput => {
  return {
    title: lesson.title,
    education_level: lesson.education_level,
    objectives_lesson: lesson.objectives_lesson,
    final_product_contribution: lesson.final_product_contribution,
    summary: lesson.summary,
    activities: lesson.activities
      .filter((activity) => !isActivityEmpty(activity))
      .map((activity) => {
        const baseActivity = {
          title: activity.title,
          mainDevelopmentDomains: activity.mainDevelopmentDomains || [],
          interactionTypes: activity.interactionTypes || [],
          typesOfEducationalActivities:
            activity.typesOfEducationalActivities || [],
          duration: activity.duration,
          activityType: activity.activityType,
          is_homework: activity.is_homework || false,
          has_manipulatives: activity.has_manipulatives,
          is_socioemotional: activity.is_socioemotional || false,
          socioemotional_main_goal: activity.is_socioemotional
            ? activity.socioemotional_main_goal || undefined
            : undefined,
          socioemotional_support_goal: activity.is_socioemotional
            ? activity.socioemotional_support_goal || undefined
            : undefined,
          summary: activity.summary || undefined,
          manipulative_materials_list:
            activity.manipulative_materials_list || undefined,
          active_methodology_tip: activity.active_methodology_tip || undefined,
          bloom_level: activity.bloom_level || [],
          primaria_interaction_type: activity.primaria_interaction_type || [],
          evidence_type: activity.evidence_type || [],
          activity_nature: activity.activity_nature || [],
        };

        // Si tiene manipulativos, enviar sections. Si no, enviar blocks
        if (activity.has_manipulatives) {
          return {
            ...baseActivity,
            ...alternativesPayload(activity, false),
            sections: activity.sections.map((section) => ({
              section_type: section.section_type,
              order_by: section.order_by,
              blocks: section.blocks.map((block, blockIndex) => ({
                type: block.type,
                order_by: blockIndex,
                text: block.text,
                file_id: block.file_id,
                page_number: block.page_number ?? null,
                materials: block.materials,
                active_methodology_tip: block.active_methodology_tip ?? null,
                resourceId: block.resourceId,
                resourceFileId: block.resourceFileId,
              })),
            })),
          };
        } else {
          return {
            ...baseActivity,
            blocks: activity.blocks.map((block, blockIndex) => ({
              type: block.type,
              order_by: blockIndex,
              text: block.text,
              file_id: block.file_id,
              page_number: block.page_number ?? null,
              materials: block.materials,
              active_methodology_tip: block.active_methodology_tip ?? null,
              resourceId: block.resourceId,
              resourceFileId: block.resourceFileId,
            })),
          };
        }
      }),
  };
};

export const parseLessonToUpdateLessonInput = (
  lesson: FormLessonUI,
): CreateLessonInput => {
  return {
    title: lesson.title,
    education_level: lesson.education_level,
    objectives_lesson: lesson.objectives_lesson,
    final_product_contribution: lesson.final_product_contribution,
    summary: lesson.summary,
    activities: lesson.activities
      .filter((activity) => !isActivityEmpty(activity))
      .map((activity) => {
        const baseActivity = {
          id: activity.id,
          title: activity.title,
          mainDevelopmentDomains: activity.mainDevelopmentDomains || [],
          interactionTypes: activity.interactionTypes || [],
          typesOfEducationalActivities:
            activity.typesOfEducationalActivities || [],
          duration: activity.duration,
          activityType: activity.activityType,
          is_homework: activity.is_homework || false,
          has_manipulatives: activity.has_manipulatives,
          is_socioemotional: activity.is_socioemotional || false,
          socioemotional_main_goal: activity.is_socioemotional
            ? activity.socioemotional_main_goal || undefined
            : undefined,
          socioemotional_support_goal: activity.is_socioemotional
            ? activity.socioemotional_support_goal || undefined
            : undefined,
          summary: activity.summary || undefined,
          manipulative_materials_list:
            activity.manipulative_materials_list || undefined,
          active_methodology_tip: activity.active_methodology_tip || undefined,
          bloom_level: activity.bloom_level || [],
          primaria_interaction_type: activity.primaria_interaction_type || [],
          evidence_type: activity.evidence_type || [],
          activity_nature: activity.activity_nature || [],
        };

        const isNewActivity = activity.id && activity.id >= 1000000000;

        // Si tiene manipulativos
        if (activity.has_manipulatives) {
          // Si es actividad nueva, enviar sections completas
          if (isNewActivity) {
            return {
              ...baseActivity,
              ...alternativesPayload(activity, true),
              sections: activity.sections.map((section) => ({
                section_type: section.section_type,
                order_by: section.order_by,
                blocks: section.blocks.map((block, blockIndex) => ({
                  type: block.type,
                  order_by: blockIndex,
                  text: block.text,
                  file_id: block.file_id,
                  page_number: block.page_number ?? null,
                  materials: block.materials,
                  active_methodology_tip: block.active_methodology_tip ?? null,
                  resourceId: block.resourceId,
                  resourceFileId: block.resourceFileId,
                })),
              })),
            };
          } else {
            // Si es actividad existente del backend, enviar sections completas con IDs
            return {
              ...baseActivity,
              ...alternativesPayload(activity, true),
              sections: activity.sections.map((section) => ({
                id: section.id,
                section_type: section.section_type,
                order_by: section.order_by,
                blocks: section.blocks.map((block) => ({
                  id: block.id,
                  type: block.type,
                  order_by: block.order_by,
                  text: block.text,
                  file_id: block.file_id,
                  page_number: block.page_number ?? null,
                  materials: block.materials,
                  active_methodology_tip: block.active_methodology_tip ?? null,
                  resourceId: block.resourceId,
                  resourceFileId: block.resourceFileId,
                })),
              })),
            };
          }
        } else {
          return {
            ...baseActivity,
            blocks: activity.blocks.map((block) => ({
              id: block.id,
              type: block.type,
              order_by: block.order_by,
              text: block.text,
              file_id: block.file_id,
              page_number: block.page_number ?? null,
              materials: block.materials,
              active_methodology_tip: block.active_methodology_tip ?? null,
              resourceId: block.resourceId,
              resourceFileId: block.resourceFileId,
            })),
          };
        }
      }),
  };
};

export const getActivitiesByType = (
  lesson: FormLessonUI,
  type: ActivityType,
) => {
  return lesson.activities.filter((activity) => activity.activityType === type);
};

export const isBlockFilled = (block: FormBlockUI) => {
  return (
    (block.text && block.text.trim().length > 0) ||
    Boolean(block.file_id) ||
    Boolean(block.resourceId) ||
    (block.materials && block.materials.trim().length > 0)
  );
};

export const isActivityEmpty = (activity: FormActivityUI) => {
  const hasTitle = activity.title.trim().length > 0;
  const hasDuration = Boolean(activity.duration && activity.duration > 0);

  const hasBlocks = activity.has_manipulatives
    ? activity.sections.some((section) =>
        section.blocks.some((block) => isBlockFilled(block)),
      )
    : activity.blocks.some((block) => isBlockFilled(block));

  return !hasTitle && !hasDuration && !hasBlocks;
};

export const validateForm = (
  lesson: FormLessonUI,
  setActiveTab: React.Dispatch<React.SetStateAction<string>>,
  options?: { skipStageActivityCheck?: boolean },
) => {
  if (!lesson.education_level) {
    return {
      validate: false,
      message: "Error de validación",
      description: "Debe seleccionar un grado para la lección.",
    };
  }
  if (!lesson.title.trim()) {
    return {
      validate: false,
      message: "Error de validación",
      description: "El título de la lección es obligatorio",
    };
  }

  const activitiesToValidate = lesson.activities.filter(
    (activity) => !isActivityEmpty(activity),
  );

  // Validación eliminada: ahora se permite submit sin actividades

  for (let i = 0; i < activitiesToValidate.length; i++) {
    const activity = activitiesToValidate[i];

    const activitiesOfSameType = lesson.activities.filter(
      (a) => a.activityType === activity.activityType,
    );
    const typeIndex =
      activitiesOfSameType.findIndex((a) => a.id === activity.id) + 1;

    if (!activity.title.trim()) {
      setActiveTab(activity.activityType);
      return {
        validate: false,
        message: "Error de validación",
        description: `El título de la actividad ${typeIndex} en ${resolveActivityType(
          activity.activityType,
        )} es obligatorio`,
      };
    }

    if (!activity.duration || activity.duration <= 0) {
      setActiveTab(activity.activityType);
      return {
        validate: false,
        message: "Error de validación",
        description: `La duración de la actividad ${typeIndex} en ${resolveActivityType(
          activity.activityType,
        )} debe ser mayor a 0`,
      };
    }

    // Validar bloques o secciones según has_manipulatives
    if (activity.has_manipulatives) {
      // Validar secciones
      if (!activity.sections || activity.sections.length === 0) {
        setActiveTab(activity.activityType);
        return {
          validate: false,
          message: "Error de validación",
          description: `La actividad ${typeIndex} en ${resolveActivityType(
            activity.activityType,
          )} debe tener al menos una sección`,
        };
      }

      // Validar bloques dentro de cada sección
      for (let s = 0; s < activity.sections.length; s++) {
        const section = activity.sections[s];
        const sectionTitle = SECTION_TITLES[section.section_type];

        if (!section.blocks || section.blocks.length === 0) {
          setActiveTab(activity.activityType);
          return {
            validate: false,
            message: "Error de validación",
            description: `La sección "${sectionTitle}" de la actividad ${typeIndex} en ${resolveActivityType(
              activity.activityType,
            )} debe tener al menos un bloque`,
          };
        }

        for (let j = 0; j < section.blocks.length; j++) {
          const block = section.blocks[j];
          const blockConfig = BLOCK_TYPE_OPTIONS[block.type] || {};

          if (blockConfig.hasImage && !block.file_id) {
            setActiveTab(activity.activityType);
            return {
              validate: false,
              message: "Error de validación",
              description: `La imagen del bloque ${
                j + 1
              } en la sección "${sectionTitle}" de la actividad ${typeIndex} (${resolveActivityType(
                activity.activityType,
              )}) es obligatoria`,
            };
          }

          if (blockConfig.hasResource && !block.resourceId) {
            setActiveTab(activity.activityType);
            return {
              validate: false,
              message: "Error de validación",
              description: `El recurso del bloque ${
                j + 1
              } en la sección "${sectionTitle}" de la actividad ${typeIndex} (${resolveActivityType(
                activity.activityType,
              )}) es obligatorio`,
            };
          }
        }
      }
    } else {
      // Validar bloques normales
      for (let j = 0; j < activity.blocks.length; j++) {
        const block = activity.blocks[j];
        const blockConfig = BLOCK_TYPE_OPTIONS[block.type] || {};

        if (blockConfig.hasText && !block.text?.trim()) {
          setActiveTab(activity.activityType);
          return {
            validate: false,
            message: "Error de validación",
            description: `El texto del bloque ${
              j + 1
            } en la actividad ${typeIndex} (${resolveActivityType(
              activity.activityType,
            )}) es obligatorio`,
          };
        }

        if (blockConfig.hasImage && !block.file_id) {
          setActiveTab(activity.activityType);
          return {
            validate: false,
            message: "Error de validación",
            description: `La imagen del bloque ${
              j + 1
            } en la actividad ${typeIndex} (${resolveActivityType(
              activity.activityType,
            )}) es obligatoria`,
          };
        }

        if (blockConfig.hasResource && !block.resourceId) {
          setActiveTab(activity.activityType);
          return {
            validate: false,
            message: "Error de validación",
            description: `El recurso del bloque ${
              j + 1
            } en la actividad ${typeIndex} (${resolveActivityType(
              activity.activityType,
            )}) es obligatorio`,
          };
        }
      }
    }
  }

  // Si es proyecto (tiene contribución al producto final), NO obligar actividades en cada bloque.
  // Las lecciones diagnóstico tampoco exigen una actividad
  // por tipo, pero sí al menos una actividad en total.
  const isProyecto =
    !!lesson.final_product_contribution &&
    lesson.final_product_contribution.trim() !== "";
  if (options?.skipStageActivityCheck) {
    if ((lesson.activities?.length ?? 0) === 0) {
      setActiveTab(ActivityType.START);
      return {
        validate: false,
        message: "Error de validación",
        description: "Debe haber al menos una actividad en la lección",
      };
    }
  } else if (!isProyecto) {
    // Check if there's at least one activity of each type
    const activityTypes = [
      ActivityType.START,
      ActivityType.MIDDLE,
      ActivityType.CLOSING,
    ];
    for (const type of activityTypes) {
      if (getActivitiesByType(lesson, type).length === 0) {
        setActiveTab(type);
        return {
          validate: false,
          message: "Error de validación",
          description: `Debe haber al menos una actividad de tipo ${resolveActivityType(
            type,
          )}`,
        };
      }
    }
  }

  return {
    validate: true,
    message: "",
    description: "",
  };
};
